import type { ComponentProps, ReactNode } from 'react';
import { Link } from 'react-router';
import { ctaClasses, type CtaSize, type CtaVariant } from './siteHelpers';

/**
 * Building blocks of the public site (docs/design/site): every page uses these instead of
 * repeating the classes, so the look stays in one place.
 */

/** Centred content column (1240 px in the design). */
export function Container({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return <div className={`relative mx-auto w-full max-w-[1240px] ${className}`}>{children}</div>;
}

type Tone = 'light' | 'deep' | 'dark';
const toneClasses: Record<Tone, string> = {
  light: 'bg-site-ground text-site-ink',
  deep: 'bg-site-ground-deep text-site-ink',
  dark: 'bg-site-night text-site-cream',
};

/** Full-width section, 120 px vertical rhythm on desktop, 72 px on phones. */
export function Section({
  id,
  tone = 'light',
  topo = false,
  className = '',
  children,
}: {
  id?: string;
  tone?: Tone;
  /** Contour pattern behind the content (dark sections in the design). */
  topo?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      className={`relative scroll-mt-[76px] overflow-hidden px-5 py-18 md:px-10 md:py-30 ${toneClasses[tone]} ${className}`}
    >
      {topo && (
        <div
          aria-hidden="true"
          className="site-topo pointer-events-none absolute inset-0 opacity-50"
        />
      )}
      {children}
    </section>
  );
}

/** Small uppercase label above a title. */
export function Eyebrow({
  tone = 'olive',
  className = '',
  children,
}: {
  tone?: 'olive' | 'clay';
  className?: string;
  children: ReactNode;
}) {
  return (
    <p
      className={`m-0 mb-4.5 font-site-display text-xs font-semibold tracking-[0.28em] uppercase ${tone === 'clay' ? 'text-site-clay-light' : 'text-site-olive'} ${className}`}
    >
      {children}
    </p>
  );
}

const display = 'm-0 font-site-display font-bold uppercase leading-[0.95] tracking-[0.01em]';

export function H1({ className = '', children }: { className?: string; children: ReactNode }) {
  return <h1 className={`${display} text-[44px] md:text-[76px] ${className}`}>{children}</h1>;
}

export function H2({
  size = 'lg',
  className = '',
  children,
}: {
  size?: 'lg' | 'md';
  className?: string;
  children: ReactNode;
}) {
  const s = size === 'lg' ? 'text-[38px] md:text-[60px]' : 'text-[34px] md:text-[48px]';
  return <h2 className={`${display} ${s} ${className}`}>{children}</h2>;
}

export function H3({ className = '', children }: { className?: string; children: ReactNode }) {
  return <h3 className={`${display} leading-[1.05] ${className}`}>{children}</h3>;
}

/** Call-to-action link: in-app route (`to`) or plain href (external, file, anchor). */
export function Cta({
  variant = 'clay',
  size = 'md',
  className = '',
  ...rest
}: { variant?: CtaVariant; size?: CtaSize; className?: string } & (
  ComponentProps<typeof Link> | (ComponentProps<'a'> & { to?: undefined })
)) {
  const cls = `${ctaClasses(variant, size)} ${className}`;
  if ('to' in rest && rest.to !== undefined) return <Link {...rest} className={cls} />;
  return <a {...(rest as ComponentProps<'a'>)} className={cls} />;
}

/** FAQ-style disclosure (`<details>`), "+" turning into "×" when open. */
export function Disclosure({
  summary,
  defaultOpen = false,
  dark = false,
  children,
}: {
  summary: ReactNode;
  defaultOpen?: boolean;
  dark?: boolean;
  children: ReactNode;
}) {
  return (
    <details
      open={defaultOpen}
      className={`site-details border-b py-5.5 ${dark ? 'border-site-cream/20' : 'border-site-line-strong'}`}
    >
      <summary
        className={`flex items-center justify-between gap-4 font-site-display text-lg tracking-[0.04em] uppercase md:text-xl ${dark ? 'text-site-cream' : ''}`}
      >
        {summary}
        <span
          aria-hidden="true"
          className={`site-plus flex-none text-2xl leading-none transition-transform ${dark ? 'text-site-clay-pale' : 'text-site-clay'}`}
        >
          +
        </span>
      </summary>
      <div
        className={`mt-3.5 text-base leading-relaxed ${dark ? 'text-site-cream/80' : 'text-site-ink-body'}`}
      >
        {children}
      </div>
    </details>
  );
}
